import React from 'react'
import { useChannelType } from 'hooks/useChannelType'
import { staticAssertNever } from 'use-towns-client'

import { RenderEvent, RenderEventType } from '../MessageTimeline/util/getEventsByDate'

import { AccumulatedStreamMemberEvent } from './items/AccumulatedMembers'
import { TimelineChannelCreateEvent } from './items/ChannelCreated'
import { TimelineGenericEvent } from './items/GenericItem'
import { MessageItem } from './items/MessageItem/MessageItem'
import { MissingMessageItem } from './items/MissingMessageItem'
import { RoomCreate } from './items/RoomCreate'
import { RoomProperties } from './items/RoomProperties'
import { TimelineThreadUpdates } from './items/ThreadUpdates'

export const MessageTimelineItem = React.memo(
    (props: {
        itemData: RenderEvent
        highlight?: boolean
        channelName?: string
        channelEncrypted?: boolean
        userId?: string
        channelType?: ReturnType<typeof useChannelType>
    }) => {
        const {
            itemData,
            channelType,
            channelEncrypted,
            highlight: isHighlight,
            channelName,
            userId,
        } = props

        switch (itemData.type) {
            case RenderEventType.EncryptedMessage:
            case RenderEventType.RedactedMessage:
            case RenderEventType.TokenTransfer:
            case RenderEventType.Message:
            case RenderEventType.InteractionRequest: {
                return (
                    <MessageItem itemData={itemData} key={itemData.key} isHighlight={isHighlight} />
                )
            }

            case RenderEventType.AccumulatedStreamMembers: {
                return (
                    <AccumulatedStreamMemberEvent
                        event={itemData}
                        key={itemData.key}
                        channelType={channelType}
                        channelEncrypted={channelEncrypted}
                        channelName={channelName}
                        userId={userId}
                    />
                )
            }

            case RenderEventType.StreamMember: {
                return <TimelineGenericEvent event={itemData.event} key={itemData.event.eventId} />
            }

            case RenderEventType.ChannelHeader: {
                return (
                    <TimelineChannelCreateEvent event={itemData.event} channelName={channelName} />
                )
            }

            case RenderEventType.Inception: {
                return (
                    <RoomCreate
                        event={itemData.event}
                        key={itemData.key}
                        userId={userId}
                        channelName={channelName}
                    />
                )
            }

            case RenderEventType.ChannelProperties: {
                return <RoomProperties event={itemData.event} userId={userId} />
            }

            case RenderEventType.ThreadUpdate: {
                return <TimelineThreadUpdates events={itemData.events} key={itemData.key} />
            }

            case RenderEventType.NewDivider: {
                return null
            }

            case RenderEventType.UserMessages: {
                /* UserMessages (grouped per user) are flatmapped into Message events */
                return null
            }

            case RenderEventType.MissingMessage: {
                return <MissingMessageItem key={itemData.key} />
            }

            default: {
                staticAssertNever(itemData)
                return null
            }
        }
    },
)
